import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Home, Building2, User, UserPlus, Pencil, History, Link2 } from "lucide-react";
import { clientLabel } from "./handoverApi";
import { StartWalkthroughButton } from "./StartWalkthroughButton";
import { SignoffPdfButton } from "./SignoffPdfButton";

const ClientBadge = ({ client, id }) => {
  if (!client) return <span className="text-sm text-slate-400 italic" data-testid={`dwelling-client-none-${id}`}>No client yet</span>;
  const isDev = client.client_type === "developer";
  const Icon = isDev ? Building2 : User;
  return (
    <div className="min-w-0" data-testid={`dwelling-client-${id}`}>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
        isDev ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`dwelling-client-type-${id}`}>
        <Icon className="w-3 h-3" />{isDev ? "Developer" : "Purchaser"}
      </span>
      <p className="text-sm font-medium text-slate-900 break-words mt-0.5" data-testid={`dwelling-client-name-${id}`}>{clientLabel(client)}</p>
      <p className="text-xs text-slate-500 break-all">{isDev ? client.developer?.email : client.purchaser?.email}</p>
    </div>
  );
};

const DwellingRow = ({ d, checked, onCheck, onSetClient, onHistory, onShare }) => (
  <div className="flex items-start lg:items-center gap-3 py-3" data-testid={`dwelling-client-row-${d.id}`}>
    <Checkbox checked={checked} onCheckedChange={(v) => onCheck(d.id, v)} aria-label={`Select ${d.dwelling_number}`} className="mt-1 lg:mt-0" data-testid={`dwelling-select-${d.id}`} />
    <div className="flex-1 min-w-0 lg:flex lg:items-center lg:gap-3">
      <div className="lg:w-44 shrink-0 flex items-center gap-2">
        <Home className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="font-semibold text-slate-900 break-words" data-testid={`dwelling-name-${d.id}`}>{d.dwelling_number}</span>
      </div>
      <div className="flex-1 min-w-0 lg:min-w-[180px] mt-1.5 lg:mt-0"><ClientBadge client={d.client} id={d.id} /></div>
      {d.client && (
        <div className="flex flex-wrap items-center gap-2 mt-2 lg:mt-0 lg:justify-end min-w-0">
          <StartWalkthroughButton dwellingId={d.id} openWalkthroughId={d.open_walkthrough_id} size="sm" testId={`start-walkthrough-${d.id}`} />
          {d.client_walkthrough_count > 0 && (
            <Button size="sm" variant="outline" onClick={() => onShare(d)} className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid={`share-link-${d.id}`}>
              <Link2 className="w-3.5 h-3.5 mr-1" />Share Link
            </Button>
          )}
          {d.latest_signoff && <SignoffPdfButton signoff={d.latest_signoff} testId={`signoff-pdf-${d.id}`} />}
          {d.walkthrough_count > 0 && (
            <Button size="sm" variant="ghost" onClick={() => onHistory(d)} className="text-slate-600" data-testid={`walkthrough-history-${d.id}`}>
              <History className="w-3.5 h-3.5 mr-1" />{d.walkthrough_count}
            </Button>
          )}
        </div>
      )}
    </div>
    <Button size="sm" variant="outline" onClick={() => onSetClient(d)} className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid={`dwelling-set-client-${d.id}`}>
      {d.client ? <><Pencil className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Change</span></> : <><UserPlus className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Set Client</span></>}
    </Button>
  </div>
);

export const DwellingClientsList = ({ dwellings, selected, onSelect, onSelectAll, onSetClient, onHistory, onShare, bulkBar }) => {
  const allChecked = dwellings.length > 0 && selected.length === dwellings.length;
  const withClient = dwellings.filter((d) => d.client).length;
  return (
    <Card className="dashboard-card" data-testid="dwelling-clients-panel">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg">Dwelling Clients</CardTitle>
        <p className="text-sm text-slate-600" data-testid="dwelling-clients-summary">{withClient} of {dwellings.length} dwellings have a client</p>
      </CardHeader>
      <CardContent>
        {dwellings.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="dwelling-clients-empty">No dwellings on this site yet. Add dwellings in Defects Details first.</p>
        ) : (
          <>
            <label className="flex items-center gap-3 pb-2 border-b border-slate-200 text-sm font-medium text-slate-600 cursor-pointer">
              <Checkbox checked={allChecked} onCheckedChange={onSelectAll} data-testid="dwelling-select-all" /> Select all
            </label>
            {bulkBar}
            <div className="divide-y divide-slate-100">
              {dwellings.map((d) => (
                <DwellingRow key={d.id} d={d} checked={selected.includes(d.id)} onCheck={onSelect} onSetClient={onSetClient} onHistory={onHistory} onShare={onShare} />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
